/**
 * WidgetShell — de gedeelde "huls" rond elke widget. Dit is het widget-
 * designprincipe in code: surface, header (icoon-tegel + titel) en een body.
 *
 * In edit-modus is de HELE kaart het sleep-handvat (de body toont dan een
 * skeleton, geen echte inhoud) en verschijnt een chrome-balk met breedte-stepper,
 * hoogte-stepper, instellingen en verwijderen. Schalen gaat puur via die knoppen
 * (geen sleep-randen). Buiten edit-modus is de widget volledig functioneel en
 * toont géén chrome. Zie docs/widget-design-principles.md.
 *
 * Presentational: alle mutaties lopen via callbacks; WidgetBoard bezit de state.
 */
import React from 'react';
import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { T, Icon, MAX_COLS, HEIGHT_ORDER, HEIGHT_LABEL, bodyHeightPx } from './widgetTokens';

function WidthStepper({ w, minW, maxW, cols, onSet }) {
  return (
    <Box sx={{ display: 'inline-flex', bgcolor: T.surfaceLow, borderRadius: '0.5rem', p: '2px' }}>
      {Array.from({ length: MAX_COLS }, (_, idx) => idx + 1).map((n) => {
        const disabled = n < minW || n > maxW || n > cols;
        const active = w === n;
        return (
          <Box
            key={n}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); if (!disabled) onSet(n); }}
            sx={{
              width: 22, height: 22, borderRadius: '0.38rem',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 700,
              cursor: disabled ? 'default' : 'pointer',
              color: active ? '#fff' : disabled ? T.outlineVariant : T.secondary,
              bgcolor: active ? T.primary : 'transparent',
              opacity: disabled ? 0.4 : 1,
              transition: 'all 0.12s',
              '&:hover': disabled || active ? {} : { bgcolor: T.surfaceHigh },
            }}
          >
            {n}
          </Box>
        );
      })}
    </Box>
  );
}

function HeightStepper({ h, allowed, onSet }) {
  const order = HEIGHT_ORDER.filter((m) => allowed.includes(m));
  const next = () => {
    const idx = order.indexOf(h);
    onSet(order[(idx + 1) % order.length]);
  };
  return (
    <Tooltip title={`Hoogte: ${HEIGHT_LABEL[h] || h} — klik om te wisselen`} arrow>
      <Box
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); next(); }}
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5,
          bgcolor: T.surfaceLow, borderRadius: '0.5rem', px: 1, height: 26, cursor: 'pointer',
          color: T.secondary, transition: 'all 0.12s', '&:hover': { bgcolor: T.surfaceHigh },
        }}
      >
        <Icon name="height" size={15} />
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 700 }}>
          {HEIGHT_LABEL[h] || h}
        </Typography>
      </Box>
    </Tooltip>
  );
}

export default function WidgetShell({
  meta, widget, editMode, cols, isDragging,
  dragHandleProps = {}, hasSettings,
  onSetWidth, onSetHeight, onRemove, onOpenSettings,
  children,
}) {
  const navigate = useNavigate();
  const accent = meta.accent || { bg: T.primaryContainer, color: T.primary };
  const bodyH = bodyHeightPx(widget.h);

  // Blank-widgets (lege ruimte): transparant en zonder header in normale weergave;
  // in edit-modus krijgen ze wél de gewone kaart + chrome zodat je ze kunt beheren.
  const blank = !!meta.blank;
  const showSurface = !blank || editMode;
  const showHeader = !blank || editMode;

  const stop = (e) => e.stopPropagation();

  return (
    <Box
      {...(editMode ? dragHandleProps : {})}
      sx={{
        position: 'relative', height: '100%',
        display: 'flex', flexDirection: 'column',
        bgcolor: showSurface ? T.surface : 'transparent',
        borderRadius: '0.9rem',
        boxShadow: showSurface ? T.shadow : 'none',
        overflow: 'hidden',
        outline: editMode ? `1px dashed ${T.outlineVariant}` : 'none',
        outlineOffset: '-1px',
        opacity: isDragging ? 0.4 : 1,
        cursor: editMode ? 'grab' : 'default',
        touchAction: editMode ? 'none' : 'auto',
        userSelect: editMode ? 'none' : 'auto',
        transition: 'box-shadow 0.15s, opacity 0.12s',
        '&:active': editMode ? { cursor: 'grabbing' } : {},
        '&:hover': editMode ? { boxShadow: T.shadowHover } : {},
      }}
    >
      {/* ── Header ── */}
      {showHeader && (
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.25,
        px: 2, pt: 1.75, pb: 1.25, flexShrink: 0,
      }}>
        {editMode && <Icon name="drag_indicator" size={18} sx={{ color: T.outlineVariant, marginLeft: '-6px' }} />}
        <Box sx={{
          width: 30, height: 30, borderRadius: '0.55rem', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: accent.bg, color: accent.color,
        }}>
          <Icon name={meta.icon} size={18} fill />
        </Box>
        <Typography sx={{
          flex: 1, minWidth: 0,
          fontFamily: T.font, fontSize: '0.92rem', fontWeight: 700, color: T.text,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {meta.title}
        </Typography>
        {!editMode && meta.fullPageHref && (
          <Tooltip title="Volledige tool openen" arrow>
            <IconButton size="small" onClick={() => navigate(meta.fullPageHref)}
              sx={{ color: T.outline, mr: -0.5, flexShrink: 0, '&:hover': { color: accent.color } }}>
              <Icon name="open_in_full" size={16} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      )}

      {/* Verwijderen — rechtsboven in de hoek (alleen edit-modus) */}
      {editMode && (
        <Tooltip title="Widget verwijderen" arrow>
          <IconButton size="small" onPointerDown={stop} onClick={(e) => { stop(e); onRemove(); }}
            sx={{
              position: 'absolute', top: 6, right: 6, zIndex: 3,
              color: T.outline, bgcolor: 'rgba(255,255,255,0.85)', boxShadow: T.shadow,
              '&:hover': { color: '#ef4444', bgcolor: '#fff' },
            }}>
            <Icon name="close" size={16} />
          </IconButton>
        </Tooltip>
      )}

      {/* ── Edit-chrome (knoppen; klikken start geen sleep) ── */}
      {editMode && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', px: 2, pb: 1.25 }}>
          <WidthStepper w={widget.w} minW={meta.minW} maxW={meta.maxW} cols={cols} onSet={onSetWidth} />
          <HeightStepper h={widget.h} allowed={meta.allowedHeights} onSet={onSetHeight} />
          <Box sx={{ flex: 1 }} />
          {hasSettings && (
            <Tooltip title="Instellingen" arrow>
              <IconButton size="small" onPointerDown={stop}
                onClick={(e) => { stop(e); onOpenSettings(e.currentTarget); }} sx={{ color: T.outline }}>
                <Icon name="settings" size={16} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {/* ── Body ── */}
      {/* Vaste hoogtes zijn een minHeight (niet hard): de widget rekt mee met de
          rij wanneer een buur hoger is, en scrollt intern bij overflow. */}
      <Box sx={{
        flex: 1, minHeight: bodyH ? `${bodyH}px` : 0,
        overflowY: editMode ? 'hidden' : (bodyH ? 'auto' : 'visible'),
        px: 2, pb: 2,
        pointerEvents: editMode ? 'none' : 'auto',
      }}>
        {children}
      </Box>
    </Box>
  );
}
